import { AbsoluteCenter, Button, Heading, Stack, Text } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Show } from '$/components/common/Flow/Show';
import { Icon } from '$/components/common/Icon';
import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { MaterialDetailsModal } from '$/components/core/Collection/MaterialDetailsModal';
import { AuthActionCard } from '$/components/core/Projects/components/Cards/AuthActionCard';
import { useIsMobile } from '$/hooks/useIsMobile';
import { BackToTopButton } from '$/pages/DashboardPages/components/BackToTopButton';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { FavoriteDashboardContent } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteDashboardContent';
import { FavoriteFolderView } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteFolderView';
import { FavoriteModal } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteModal';
import { Material } from '$/services/usecases/materials';
import { useFavoriteStore } from '$/stores/useFavoriteStore';

export type FavoriteItem = {
  material: Material;
  parentFolders: string[];
};

export const FavoriteDashboard = () => {
  const { t } = useTranslation();
  const activeFolderId = useFavoriteStore.useActiveFolderId();
  const favorites = useFavoriteStore.useFavorites();
  const { hasPermission } = useAuthorization();
  const isMobile = useIsMobile();

  const ref = useRef<HTMLDivElement>(null);

  if (!hasPermission('Favorites_Open')) {
    return (
      <Stack
        pos='relative'
        gap='0'
        overflowY='auto'
        w='full'
        h='full'
        {...(!isMobile && {
          layerStyle: 'DashboardSection',
          p: '7',
        })}
      >
        <Heading as='h1' mb='8' fontSize='3xl'>
          {t('favorites.pageHeader')}
        </Heading>
        <DashboardSectionRow>
          <AuthActionCard type='loginFavorite' />
        </DashboardSectionRow>
      </Stack>
    );
  }

  if (favorites.length === 0) {
    return (
      <Stack
        pos='relative'
        gap='0'
        overflowY='auto'
        w='full'
        h='full'
        {...(!isMobile && {
          layerStyle: 'DashboardSection',
          p: '7',
        })}
      >
        <Heading as='h1' mb='8' fontSize='3xl'>
          {t('favorites.pageHeader')}
        </Heading>
        <AbsoluteCenter>
          <Stack
            alignItems='center'
            gap='6'
            p='8'
            bg='bodyBackground'
            border='1px solid'
            borderColor='border'
            borderRadius='12px'
          >
            <Heading as='h2' fontSize='xl'>
              {t('favorites.empty.title')}
            </Heading>
            <Icon icon='like' boxSize='3rem' fill='transparent' />
            <Text fontSize='sm' whiteSpace='pre-line'>
              {t('favorites.empty.description')}
            </Text>

            <Button as={Link} to='/collections' variant='black'>
              {t('favorites.empty.discoverCollections')}
            </Button>
          </Stack>
        </AbsoluteCenter>
      </Stack>
    );
  }

  return (
    <Stack
      ref={ref}
      gap='0'
      overflowY='auto'
      w='full'
      {...(!isMobile && {
        layerStyle: 'DashboardSection',
        p: '7',
      })}
    >
      <Show when={activeFolderId == null} fallback={FavoriteFolderView}>
        <FavoriteDashboardContent sectionRef={ref} />
      </Show>
      <MaterialDetailsModal />
      <FavoriteModal />

      <BackToTopButton
        containerRef={ref}
        right='65px'
        bottom='65px'
      ></BackToTopButton>
    </Stack>
  );
};
